/**
 * description-rule-api — 백엔드 /api/admin/description-rules (hcltech-parts DescriptionRule.Routes · CLAUDE.md V장 "디스크립션 규칙" · 2026-10-23).
 *   약식 디스크립션 규칙 한 벌 = description_rule 한 행(카테고리 · 이름 · 자리 순서 pattern · 꼬리 처리 tail · 기본 · 버전).
 *   목록 · 한 건 · 만들기 · 고치기 · 지우기 · 기본 교체 · 버전 목록 · 되돌리기 · 미리보기 · 영향 수 · 한 행의 규칙 고르기.
 *   스키마는 백엔드 DescriptionRule.Types 와 같은 철자 — 다르면 Zod 가 경계에서 막는다.
 */
import { z } from 'zod';
import { deleteJson, getJson, postJson, putJson } from './http';
import { E_CATALOG_TABLES } from './catalog-request-api';
import type { ECatalogTable } from './catalog-request-api';

const BASE = '/api/admin/description-rules';

// ─── 열거 — 백엔드와 같은 철자 ───────────────────────────────────────
/** 토큰의 역할 22 — 원문을 단어로 갈라 붙인 이름. misc = 역할 없음 */
export const E_DESC_TOKEN_ROLES = [
  'vendor', 'brand', 'family', 'model', 'generation',
  'form_factor', 'rack_unit', 'bay',
  'capacity', 'memory_type', 'speed', 'interface', 'rpm',
  'cores', 'clock', 'threads', 'cache', 'ports', 'wattage', 'rank',
  'kind', 'misc',
] as const;
export type  EDescTokenRole = typeof E_DESC_TOKEN_ROLES[number];

/** 역할 없는 토큰(misc) 처리 — drop 버림 · append 꼬리에 붙임 */
export const E_RULE_TAILS = ['drop', 'append'] as const;
export type  ERuleTail    = typeof E_RULE_TAILS[number];

export const TOKEN_ROLE_WORD: Record<EDescTokenRole, string> = {
  vendor: '제조사', brand: '브랜드', family: '제품군', model: '모델', generation: '세대',
  form_factor: '폼팩터', rack_unit: '랙 높이(U)', bay: '베이',
  capacity: '용량', memory_type: '메모리 종류', speed: '속도', interface: '인터페이스', rpm: '회전수',
  cores: '코어', clock: '클럭', threads: '스레드', cache: '캐시', ports: '포트', wattage: '와트', rank: '랭크',
  kind: '종류(SSD · HDD · Tray)', misc: '역할 없음',
};
export const RULE_TAIL_WORD: Record<ERuleTail, string> = { drop: '규칙에 없는 말은 버림', append: '규칙에 없는 말은 원문 순서로 뒤에 붙임' };

// ─── 스키마 ───────────────────────────────────────────────────────────
/** 자리 하나 — 그 역할의 토큰을 어떻게 적나(앞말 · 뒷말 · 필수 — 비면 등록을 막고 약식을 비운다) */
export const TokenFormatSchema = z.object({
  kind: z.literal('token'),
  role: z.enum(E_DESC_TOKEN_ROLES),
  prefix: z.string(),
  suffix: z.string(),
  required: z.boolean(),
});
export type TokenFormat = z.infer<typeof TokenFormatSchema>;

/** 묶음 — 안 자리들을 앞말 · 뒷말로 감싼다. 안 자리가 하나라도 읽히면 감싸고 다 비면 괄호째 뺀다(백엔드 2026-10-26) */
export const TokenGroupSchema = z.object({
  kind: z.literal('group'),
  prefix: z.string(),
  suffix: z.string(),
  formats: z.array(TokenFormatSchema).min(1),   // 묶음 안에 묶음은 없다
});
export type TokenGroup = z.infer<typeof TokenGroupSchema>;

/** 규칙 항목 — pattern 의 한 칸, 자리 또는 묶음(백엔드 PATTERN_ITEM_SCHEMA) */
export const PatternItemSchema = z.discriminatedUnion('kind', [TokenFormatSchema, TokenGroupSchema]);
export type PatternItem = z.infer<typeof PatternItemSchema>;

export const DescriptionRuleViewSchema = z.object({
  id: z.string(),
  spec_category: z.string(),
  name: z.string(),
  pattern: z.array(PatternItemSchema),
  tail: z.enum(E_RULE_TAILS),
  is_default: z.boolean(),
  version: z.number().int(),
  updated_at: z.string(),
});
export type DescriptionRuleView = z.infer<typeof DescriptionRuleViewSchema>;

/** 만들기 · 고치기 입력 */
export interface DescriptionRuleInput {
  spec_category: string;
  name: string;
  pattern: PatternItem[];
  tail: ERuleTail;
}

export const DescriptionRuleVersionViewSchema = z.object({
  version: z.number().int(),
  pattern: z.array(PatternItemSchema),
  tail: z.enum(E_RULE_TAILS),
  changed_by: z.string().nullable(),
  changed_at: z.string(),
});
export type DescriptionRuleVersionView = z.infer<typeof DescriptionRuleVersionViewSchema>;

export const DescriptionPreviewLineSchema = z.object({ raw: z.string(), description: z.string().nullable() });
export type DescriptionPreviewLine = z.infer<typeof DescriptionPreviewLineSchema>;

/** 규칙 읽기 — 자리 하나에 읽은 말(text null = 비어 있음). 묶음은 펴서 안 자리 한 줄씩 */
export const SlotReadingSchema = z.object({ role: z.enum(E_DESC_TOKEN_ROLES), text: z.string().nullable(), required: z.boolean() });
export type SlotReading = z.infer<typeof SlotReadingSchema>;

/** 친 글 하나를 규칙으로 읽은 결과 — rule_id null = 그 카테고리에 규칙 없음 · description null = 필수 빔(친 글 그대로 저장) */
export const DescriptionReadingSchema = z.object({ rule_id: z.string().nullable(), slots: z.array(SlotReadingSchema), description: z.string().nullable() });
export type DescriptionReading = z.infer<typeof DescriptionReadingSchema>;

/** 사람이 채운 자리 하나 — 원문에 없는 말을 그 역할로(백엔드 SlotFill · 2026-10-26 A) */
export type SlotFill = { role: EDescTokenRole; text: string };

/** 읽기 요청 — rule_id null = 카테고리 기본 규칙. fills = 채운 자리(원문 끝에 그 역할의 말로 더해 읽는다) */
export interface DescriptionReadRequest {
  spec_category: string;
  raw: string;
  rule_id: string | null;
  fills: SlotFill[];
}

/** 다시 써질 행의 수 — 카탈로그 행 · 제품 따로 */
export const RewriteCountSchema = z.object({ catalog_rows: z.number().int(), products: z.number().int() });
export type RewriteCount = z.infer<typeof RewriteCountSchema>;

/** 규칙 하나의 영향 — chosen: 이 규칙을 고른 행 · category_default: 그 카테고리의 기본 규칙을 쓰는 행 */
export const DescriptionRuleUsageSchema = z.object({
  rule_id: z.string(),
  is_default: z.boolean(),
  chosen: RewriteCountSchema,
  category_default: RewriteCountSchema,
});
export type DescriptionRuleUsage = z.infer<typeof DescriptionRuleUsageSchema>;

/** 규칙을 고를 한 행 — 카탈로그 표 17 또는 제품 */
export interface RuleTarget {
  table: ECatalogTable | 'hclt_product';
  id: number;
}

/** 고칠 목록 한 줄 — 지금 규칙으로 다시 읽으면 필수 자리가 비는 행(서버 ListMissingRequiredRows). 카탈로그 행 먼저 · id 순 · 최대 500 */
export const DescriptionRuleMissingRowSchema = z.object({
  target: z.object({ table: z.string(), id: z.number().int() }),
  raw: z.string(),
  missing_roles: z.array(z.enum(E_DESC_TOKEN_ROLES)),
});
export type DescriptionRuleMissingRow = z.infer<typeof DescriptionRuleMissingRowSchema>;

const RowsSchema = <T extends z.ZodTypeAny>(item: T) => z.object({ rows: z.array(item) });
const DeletedSchema = z.object({ deleted: z.literal(true) });
const OkSchema = z.object({ ok: z.literal(true) });

// ─── 호출 ─────────────────────────────────────────────────────────────
export async function fetchDescriptionRules(categoryOrNull: string | null): Promise<DescriptionRuleView[]> {
  const query = categoryOrNull === null ? '' : `?category=${encodeURIComponent(categoryOrNull)}`;
  const body = await getJson(`${BASE}${query}`, { schema: RowsSchema(DescriptionRuleViewSchema) });
  return body.rows;
}

export async function fetchDescriptionRule(ruleId: string): Promise<DescriptionRuleView> {
  return getJson(`${BASE}/${encodeURIComponent(ruleId)}`, { schema: DescriptionRuleViewSchema });
}

export async function createDescriptionRule(input: DescriptionRuleInput): Promise<DescriptionRuleView> {
  return postJson(`${BASE}`, input, DescriptionRuleViewSchema);
}

export async function updateDescriptionRule(ruleId: string, input: DescriptionRuleInput): Promise<DescriptionRuleView> {
  return putJson(`${BASE}/${encodeURIComponent(ruleId)}`, input, DescriptionRuleViewSchema);
}

export async function deleteDescriptionRule(ruleId: string): Promise<void> {
  await deleteJson(`${BASE}/${encodeURIComponent(ruleId)}`, DeletedSchema);
}

export async function setDefaultDescriptionRule(ruleId: string): Promise<DescriptionRuleView> {
  return postJson(`${BASE}/${encodeURIComponent(ruleId)}/default`, {}, DescriptionRuleViewSchema);
}

export async function fetchDescriptionRuleVersions(ruleId: string): Promise<DescriptionRuleVersionView[]> {
  const body = await getJson(`${BASE}/${encodeURIComponent(ruleId)}/versions`, { schema: RowsSchema(DescriptionRuleVersionViewSchema) });
  return body.rows;
}

export async function restoreDescriptionRuleVersion(ruleId: string, version: number): Promise<DescriptionRuleView> {
  return postJson(`${BASE}/${encodeURIComponent(ruleId)}/versions/${version}/restore`, {}, DescriptionRuleViewSchema);
}

/** 저장 전 입력으로 그 카테고리 원문 limit 개를 렌더 — DB 는 안 바뀐다 */
/** 친 글 하나를 규칙으로 읽는다 — DB 는 안 바뀐다(제품 등록 폼의 규칙 읽기) */
export async function readDescription(request: DescriptionReadRequest): Promise<DescriptionReading> {
  return postJson(`${BASE}/read`, request, DescriptionReadingSchema);
}

export async function previewDescriptionRule(input: DescriptionRuleInput, limit: number): Promise<DescriptionPreviewLine[]> {
  const body = await postJson(`${BASE}/preview`, { ...input, limit }, RowsSchema(DescriptionPreviewLineSchema));
  return body.rows;
}

export async function fetchDescriptionRuleUsage(ruleId: string): Promise<DescriptionRuleUsage> {
  return getJson(`${BASE}/${encodeURIComponent(ruleId)}/usage`, { schema: DescriptionRuleUsageSchema });
}

export async function fetchMissingRequiredRows(ruleId: string): Promise<DescriptionRuleMissingRow[]> {
  const body = await getJson(`${BASE}/${encodeURIComponent(ruleId)}/missing`, { schema: RowsSchema(DescriptionRuleMissingRowSchema) });
  return body.rows;
}

/** 저장 전 — 이 입력으로 다시 쓰면 필수가 비는 행 수(서버 CountMissingRequiredRows) */
export async function countMissingRequiredRows(ruleId: string, input: DescriptionRuleInput): Promise<RewriteCount> {
  return postJson(`${BASE}/${encodeURIComponent(ruleId)}/missing-count`, input, RewriteCountSchema);
}

/** 한 행의 규칙 고르기 — null 은 기본 규칙으로. 그 행의 약식이 바로 다시 써진다.
 *  카탈로그 행이면 사유 필수(빈 글 400) · 관리자만(403) · 슬랙 알림. 제품(hclt_product)은 사유를 안 본다 — null(2026-10-27) */
export async function chooseDescriptionRule(target: RuleTarget, ruleIdOrNull: string | null, reasonNoteOrNull: string | null): Promise<void> {
  await putJson(`${BASE}/choice`, { table: target.table, id: target.id, rule_id: ruleIdOrNull, reason_note: reasonNoteOrNull }, OkSchema);
}

export function isRuleTargetTable(table: string): table is ECatalogTable | 'hclt_product' {
  return table === 'hclt_product' || (E_CATALOG_TABLES as readonly string[]).includes(table);
}
